/**
 * ParentVerifyPage
 * Route: /consent/parent/verify?token=xxx
 *
 * A parent/guardian verifies consent for a minor account.
 * Supports two verification methods: existing account OR DigiLocker.
 */

import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Shield,
  Mail,
  Smartphone,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { toast } from "sonner";
import { verifyParentConsent } from "@/services/dpdpApi";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";
const DIGILOCKER_AVAILABLE = !!process.env.REACT_APP_DIGILOCKER_AVAILABLE;

const buildDigiLockerUrl = (token) =>
  `${BASE_URL}/api/v1/dpdp/consent/minor/verify/digilocker?token=${encodeURIComponent(token)}`;

export default function ParentVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [step, setStep] = useState("intro");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrorMessage(
        "No verification token provided. Please use the link from your email."
      );
      setStep("error");
    }
  }, [token]);

  const handleExistingAccountVerify = async () => {
    if (!token) return;
    setLoading(true);
    setStep("loading");
    try {
      const result = await verifyParentConsent({
        token,
        verification_method: "existing_account",
      });
      if (result.status === "verified") {
        setStep("success");
        setTimeout(() => navigate("/"), 5000);
      }
    } catch (err) {
      setErrorMessage(err.message || "Verification failed. Please try again.");
      setStep("error");
    } finally {
      setLoading(false);
    }
  };

  const handleDigiLockerVerify = () => {
    if (!token) return;
    if (!DIGILOCKER_AVAILABLE) {
      toast.error("DigiLocker verification is not yet available. Please use the Existing Account method instead.");
      return;
    }
    window.location.href = buildDigiLockerUrl(token);
  };

  if (step === "error") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            <CardTitle className="text-xl">Verification Failed</CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button variant="outline" onClick={() => navigate("/")}>
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <CardTitle className="text-xl">Consent Verified</CardTitle>
            <CardDescription>
              You have successfully verified parental consent. Your child can now
              access the platform. Redirecting...
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground">
              Redirecting to home page in 5 seconds...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-indigo-500" />
          <p className="mt-4 text-muted-foreground">Verifying consent...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-slate-900 dark:via-indigo-950 dark:to-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-indigo-600 dark:text-indigo-300" />
          </div>
          <CardTitle className="text-2xl">Parental Consent Verification</CardTitle>
          <CardDescription>
            A child account on Right Doers World requires your consent to
            proceed.
            <br />
            <span className="text-xs text-muted-foreground">
              Per DPDP Act 2023, Section 10 — Consent of guardian for children.
            </span>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {step === "intro" && (
            <>
              <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-4">
                <h3 className="font-semibold text-sm mb-2">
                  What is being consented to?
                </h3>
                <p className="text-sm text-muted-foreground">
                  By verifying, you confirm that you are the parent or guardian of
                  the child and consent to their use of the Right Doers World
                  platform, including psychometric assessments, career guidance,
                  and educational services.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  You may revoke this consent at any time by visiting the Privacy
                  Centre.
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-sm">
                  Choose verification method:
                </h3>

                <Button
                  variant="outline"
                  className="w-full h-auto py-4 justify-start text-left"
                  onClick={handleExistingAccountVerify}
                >
                  <Mail className="h-5 w-5 mr-3 text-indigo-500" />
                  <div>
                    <p className="font-medium">Existing Account</p>
                    <p className="text-xs text-muted-foreground">
                      Verify using your existing Right Doers World account
                    </p>
                  </div>
                </Button>

                {DIGILOCKER_AVAILABLE && (
                  <Button
                    variant="outline"
                    className="w-full h-auto py-4 justify-start text-left"
                    onClick={handleDigiLockerVerify}
                  >
                    <Smartphone className="h-5 w-5 mr-3 text-indigo-500" />
                    <div>
                      <p className="font-medium">DigiLocker (e-Residency)</p>
                      <p className="text-xs text-muted-foreground">
                        Verify your identity via Aadhaar through DigiLocker
                      </p>
                    </div>
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
